/** Tiny WebAudio synth: every sound effect is generated, so the game ships with no audio assets. */
export type Sfx =
  | 'jump'
  | 'spring'
  | 'land'
  | 'coin'
  | 'dive'
  | 'hurt'
  | 'stomp'
  | 'teleport'
  | 'win'
  | 'insight'
  | 'bonk'
  | 'blip'
  | 'arrive';

export class Audio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private unlocked = false;
  enabled = true;

  private ensure(): AudioContext | null {
    if (!this.enabled || !this.unlocked) return null;
    if (!this.ctx) {
      const AC = window.AudioContext ?? (window as any).webkitAudioContext;
      if (!AC) return null;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.32;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }

  /** Call from a user gesture so browsers allow audio. */
  unlock(): void {
    this.unlocked = true;
    this.ensure();
  }

  private tone(
    type: OscillatorType,
    f0: number,
    f1: number,
    dur: number,
    vol = 0.5,
    delay = 0,
  ): void {
    const ctx = this.ensure();
    if (!ctx || !this.master) return;
    const t = ctx.currentTime + delay;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(this.master);
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  private noise(dur: number, vol = 0.3, freq = 1200, delay = 0): void {
    const ctx = this.ensure();
    if (!ctx || !this.master) return;
    const t = ctx.currentTime + delay;
    const len = Math.max(1, Math.floor(ctx.sampleRate * dur));
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.value = vol;
    src.connect(filter).connect(g).connect(this.master);
    src.start(t);
  }

  play(s: Sfx): void {
    if (!this.enabled) return;
    switch (s) {
      case 'jump':
        this.tone('square', 330, 620, 0.11, 0.12);
        break;
      case 'spring':
        this.tone('sine', 220, 880, 0.28, 0.35);
        this.tone('triangle', 440, 1320, 0.2, 0.12, 0.03);
        break;
      case 'land':
        this.noise(0.06, 0.18, 700);
        break;
      case 'bonk':
        this.tone('square', 180, 120, 0.07, 0.1);
        break;
      case 'coin':
        this.tone('square', 988, 988, 0.06, 0.12);
        this.tone('square', 1319, 1319, 0.16, 0.12, 0.06);
        break;
      case 'dive':
        this.tone('sawtooth', 700, 90, 0.45, 0.16);
        this.noise(0.35, 0.12, 2400, 0.05);
        break;
      case 'arrive':
        this.tone('triangle', 200, 520, 0.3, 0.2);
        break;
      case 'hurt':
        this.noise(0.25, 0.35, 1800);
        this.tone('sawtooth', 300, 70, 0.3, 0.18);
        break;
      case 'stomp':
        this.tone('square', 600, 200, 0.12, 0.16);
        this.noise(0.08, 0.2, 1500);
        break;
      case 'teleport':
        for (let i = 0; i < 4; i++) this.tone('sine', 500 + i * 180, 900 + i * 240, 0.12, 0.1, i * 0.04);
        break;
      case 'insight':
        this.tone('sine', 660, 660, 0.25, 0.14);
        this.tone('sine', 990, 990, 0.35, 0.12, 0.08);
        break;
      case 'blip':
        this.tone('square', 740, 740, 0.04, 0.06);
        break;
      case 'win': {
        const notes = [523, 659, 784, 1047, 784, 1047, 1319];
        notes.forEach((f, i) => this.tone('square', f, f, i === notes.length - 1 ? 0.5 : 0.12, 0.13, i * 0.1));
        break;
      }
    }
  }
}
